import { useEffect, useMemo, useState, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { apiFetch, fetchInboundSummary } from '../api.js';
import Reservations from './Reservations.jsx';
import Membership from './Membership.jsx';
import AdminInvites from './AdminInvites.jsx';
import Boards from './Boards.jsx';
import AdminBroadcast from './AdminBroadcast.jsx';
import AdminApplications from './AdminApplications.jsx';
import AdminMembers from './AdminMembers.jsx';
import AdminMedia from './AdminMedia.jsx';
import AdminInstagram from './AdminInstagram.jsx';
import ContactPreferences from './ContactPreferences.jsx';
import Household from './Household.jsx';
import MemberRequests from './MemberRequests.jsx';
import AdminScheduling from './AdminScheduling.jsx';
import AdminRequests from './AdminRequests.jsx';

// Member portal. Renders the signed-in member's tools as a clean tabbed layout
// instead of one long stack of cards: a compact identity header, then member
// tabs, then (for admins) a visually separated set of admin tabs. Every panel's
// functionality is unchanged — this only reorganizes navigation.
//
// /api/me is the end-to-end auth proof: a token minted by the live CIAM tenant,
// validated by the Functions API, resolved to a member profile. Three states:
// loading, linked member (200 w/ profile), or authenticated-but-not-a-member
// (JIT case: valid identity, memberId=null until an invite is accepted).
export default function Profile() {
  const { instance } = useMsal();
  const account = instance.getActiveAccount();
  const accountKey = account?.homeAccountId || account?.username || null;
  const [state, setState] = useState({ loading: true, error: null, me: null });
  const [activeTab, setActiveTab] = useState(null);
  const [inboundCount, setInboundCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { status, ok, data } = await apiFetch(instance, account, '/api/me');
        if (cancelled) return;
        if (!ok) {
          const detail = (data && (data.message || data.error)) || `HTTP ${status}`;
          setState({ loading: false, error: `API returned ${status}: ${detail}`, me: null });
          return;
        }
        setState({ loading: false, error: null, me: data });
      } catch (e) {
        if (!cancelled) setState({ loading: false, error: e.message, me: null });
      }
    })();
    return () => { cancelled = true; };
    // Key on the stable account id, NOT the account object (new ref each render -> loop).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountKey]);

  const me = state.me || {};
  const isMember = Boolean(me.memberId);
  const isAdmin = me.role === 'admin';

  // Poll inbound message summary for admins (badge on Members tab).
  const loadInboundSummary = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await fetchInboundSummary(instance, account);
      if (res.ok) {
        const count = (res.data.summary || []).length;
        setInboundCount(count);
      }
    } catch (e) {
      // Fail silently — don't break the portal if the summary fetch fails.
    }
  }, [instance, account, isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    loadInboundSummary();
    const interval = setInterval(loadInboundSummary, 30000); // poll every 30s
    return () => clearInterval(interval);
  }, [isAdmin, loadInboundSummary]);

  // Build the tab set for this user. Each tab lazily renders its panel(s).
  const tabs = useMemo(() => {
    const list = [];
    if (isMember) {
      list.push({
        id: 'membership', label: 'My Membership', group: 'member',
        render: () => (
          <>
            <Membership />
            <Household memberId={me.memberId} />
            <ContactPreferences />
          </>
        ),
      });
      list.push({ id: 'reservations', label: 'Reservations', group: 'member',
        render: () => <Reservations /> });
      list.push({ id: 'requests', label: 'Requests', group: 'member',
        render: () => <MemberRequests /> });
      list.push({ id: 'community', label: 'Community', group: 'member',
        render: () => <Boards isAdmin={isAdmin} /> });
    }
    if (isAdmin) {
      list.push({ id: 'members', label: 'Members', group: 'admin', badge: inboundCount || null,
        render: () => <AdminMembers myMemberId={me.memberId} /> });
      list.push({ id: 'scheduling', label: 'Scheduling', group: 'admin',
        render: () => <AdminScheduling /> });
      list.push({ id: 'admin-requests', label: 'Requests', group: 'admin',
        render: () => <AdminRequests /> });
      list.push({ id: 'applications', label: 'Applications', group: 'admin',
        render: () => (<><AdminApplications /><AdminInvites /></>) });
      list.push({ id: 'broadcast', label: 'Broadcast', group: 'admin',
        render: () => <AdminBroadcast /> });
      list.push({ id: 'site-media', label: 'Site Media', group: 'admin',
        render: () => (<><AdminMedia /><AdminInstagram /></>) });
    }
    return list;
  }, [isMember, isAdmin, me.memberId]);

  // Default the active tab to the first available once tabs are known.
  useEffect(() => {
    if (tabs.length && (!activeTab || !tabs.some((t) => t.id === activeTab))) {
      setActiveTab(tabs[0].id);
    }
  }, [tabs, activeTab]);

  if (state.loading) return <div className="card">Loading your profile…</div>;

  if (state.error) {
    return (
      <div className="card error">
        <h2>Couldn't load your profile</h2>
        <p>{state.error}</p>
        <p className="muted">This usually means the API auth settings aren't wired yet, or your token was rejected.</p>
      </div>
    );
  }

  const displayName = me.name || account?.name || me.email || account?.username || 'Member';
  const roleLabel = isAdmin ? 'Admin' : (isMember ? 'Member' : 'Guest');

  // Not linked to a membership yet (JIT identity) — no tabs to show.
  if (!isMember) {
    return (
      <div className="portal">
        <PortalHeader name={displayName} email={me.email || account?.username} roleLabel={roleLabel} isAdmin={isAdmin} />
        <div className="card notice">
          <strong>You're signed in, but not linked to a membership yet.</strong>
          <p>If you have an invitation, accepting it will link your account.</p>
          <p className="muted">Your identity id (for admin linking):</p>
          <code className="ext-id">{me.externalId || '—'}</code>
        </div>
      </div>
    );
  }

  const memberTabs = tabs.filter((t) => t.group === 'member');
  const adminTabs = tabs.filter((t) => t.group === 'admin');
  const active = tabs.find((t) => t.id === activeTab) || tabs[0];

  return (
    <div className="portal">
      <PortalHeader name={displayName} email={me.email || account?.username} roleLabel={roleLabel} isAdmin={isAdmin} />

      <nav className="portal-tabs" role="tablist" aria-label="Member portal sections">
        <div className="portal-tabgroup">
          {memberTabs.map((t) => (
            <TabButton key={t.id} tab={t} active={active?.id === t.id} onClick={() => setActiveTab(t.id)} />
          ))}
        </div>
        {adminTabs.length > 0 && (
          <div className="portal-tabgroup portal-tabgroup-admin" aria-label="Admin tools">
            <span className="portal-tabgroup-label">Admin</span>
            {adminTabs.map((t) => (
              <TabButton key={t.id} tab={t} active={active?.id === t.id} onClick={() => setActiveTab(t.id)} />
            ))}
          </div>
        )}
      </nav>

      <div className="portal-panel" role="tabpanel">
        {active?.render()}
      </div>
    </div>
  );
}

function TabButton({ tab, active, onClick }) {
  return (
    <button
      role="tab"
      aria-selected={active}
      className={`portal-tab${active ? ' is-active' : ''}${tab.group === 'admin' ? ' is-admin' : ''}`}
      onClick={onClick}
    >
      {tab.label}
      {tab.badge && <span className="portal-tab-badge">{tab.badge}</span>}
    </button>
  );
}

function PortalHeader({ name, email, roleLabel, isAdmin }) {
  return (
    <header className="portal-header">
      <div className="portal-header-main">
        <div className="portal-avatar" aria-hidden="true">{(name || '?').charAt(0).toUpperCase()}</div>
        <div>
          <div className="portal-name">{name}</div>
          {email && <div className="portal-email">{email}</div>}
        </div>
      </div>
      <span className={`portal-rolebadge${isAdmin ? ' is-admin' : ''}`}>{roleLabel}</span>
    </header>
  );
}
